// AGC: FarmProHub Governance Hook
// AGC: Manages proposals and voting

import { useState, useEffect, useCallback } from 'react'
import { useAccount, useReadContract, useWriteContract } from 'wagmi'

// AGC: Governance contract ABI (minimal for now)
const GOVERNANCE_ABI = [
  {
    name: 'getProposal',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'proposalId', type: 'uint256' }],
    outputs: [
      { name: 'title', type: 'string' },
      { name: 'description', type: 'string' },
      { name: 'votesFor', type: 'uint256' },
      { name: 'votesAgainst', type: 'uint256' },
      { name: 'endTime', type: 'uint256' },
      { name: 'executed', type: 'bool' },
      { name: 'forumPostUrl', type: 'string' },
      { name: 'ideaOriginator', type: 'address' },
      { name: 'rewardAmount', type: 'uint256' },
    ],
  },
  {
    name: 'getProposalCount',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    name: 'createProposal',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'title', type: 'string' },
      { name: 'description', type: 'string' },
      { name: 'forumPostUrl', type: 'string' },
      { name: 'ideaOriginator', type: 'address' },
      { name: 'rewardAmount', type: 'uint256' },
    ],
    outputs: [{ name: 'proposalId', type: 'uint256' }],
  },
  {
    name: 'vote',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'proposalId', type: 'uint256' },
      { name: 'support', type: 'bool' },
    ],
    outputs: [],
  },
  {
    name: 'hasVoted',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'proposalId', type: 'uint256' },
      { name: 'account', type: 'address' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
  {
    name: 'getVotingPower',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const

// AGC: Contract address from env
const GOVERNANCE_CONTRACT_ADDRESS = import.meta.env.VITE_GOVERNANCE_ADDRESS as `0x${string}` | undefined

export interface Proposal {
  id: number
  title: string
  description: string
  votesFor: bigint
  votesAgainst: bigint
  endTime: number
  executed: boolean
  status: 'active' | 'passed' | 'rejected' | 'pending'
  // AGC: Forum post and reward fields
  forumPostUrl?: string
  ideaOriginator?: `0x${string}`
  rewardAmount?: bigint
}

export interface CreateProposalParams {
  title: string
  description: string
  forumPostUrl?: string
  ideaOriginator?: `0x${string}`
  rewardAmount?: bigint
}

export interface UseGovernanceReturn {
  proposals: Proposal[]
  votingPower: bigint
  proposalCount: number
  isLoading: boolean
  vote: (proposalId: number, support: boolean) => Promise<void>
  hasVoted: (proposalId: number) => Promise<boolean>
  isVoting: boolean
  createProposal: (params: CreateProposalParams) => Promise<void>
  isCreating: boolean
  refresh: () => void
}

export const useGovernance = (): UseGovernanceReturn => {
  const { address, isConnected } = useAccount()
  const [proposals, setProposals] = useState<Proposal[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isCreating, setIsCreating] = useState(false)

  // AGC: Read proposal count
  const { data: proposalCount, refetch: refetchProposalCount } = useReadContract({
    address: GOVERNANCE_CONTRACT_ADDRESS,
    abi: GOVERNANCE_ABI,
    functionName: 'getProposalCount',
    query: {
      enabled: !!GOVERNANCE_CONTRACT_ADDRESS,
    },
  })

  // AGC: Read voting power
  const { data: votingPower, refetch: refetchVotingPower } = useReadContract({
    address: GOVERNANCE_CONTRACT_ADDRESS,
    abi: GOVERNANCE_ABI,
    functionName: 'getVotingPower',
    args: address ? [address] : undefined,
    query: {
      enabled: !!GOVERNANCE_CONTRACT_ADDRESS && !!address,
    },
  })

  // AGC: Write contract functions
  const { writeContractAsync, isPending: isVoting } = useWriteContract()

  // AGC: Create proposal function
  const createProposal = useCallback(async (params: CreateProposalParams) => {
    if (!GOVERNANCE_CONTRACT_ADDRESS || !address) {
      throw new Error('Governance contract not configured or wallet not connected')
    }

    setIsCreating(true)
    try {
      // AGC: Prepare arguments - use empty string/zero address/zero for optional fields
      const args = [
        params.title,
        params.description,
        params.forumPostUrl || '',
        params.ideaOriginator || '0x0000000000000000000000000000000000000000' as `0x${string}`,
        params.rewardAmount || 0n,
      ]

      const hash = await writeContractAsync({
        address: GOVERNANCE_CONTRACT_ADDRESS,
        abi: GOVERNANCE_ABI,
        functionName: 'createProposal',
        args,
      })

      // AGC: Refresh proposals after creation
      await refetchProposalCount()
      
      return hash
    } finally {
      setIsCreating(false)
    }
  }, [address, writeContractAsync, refetchProposalCount])

  // AGC: Vote function
  const vote = useCallback(async (proposalId: number, support: boolean) => {
    if (!GOVERNANCE_CONTRACT_ADDRESS || !address) {
      throw new Error('Governance contract not configured or wallet not connected')
    }

    await writeContractAsync({
      address: GOVERNANCE_CONTRACT_ADDRESS,
      abi: GOVERNANCE_ABI,
      functionName: 'vote',
      args: [BigInt(proposalId), support],
    })
  }, [address, writeContractAsync])

  // AGC: Check if user has voted
  const hasVoted = useCallback(async (proposalId: number): Promise<boolean> => {
    if (!GOVERNANCE_CONTRACT_ADDRESS || !address) {
      return false
    }
    
    // AGC: This would need to be implemented with a contract read
    // For now, return false as placeholder
    return false
  }, [address])

  // AGC: Refresh all data
  const refresh = useCallback(() => {
    setIsLoading(true)
    Promise.all([
      refetchProposalCount(),
      refetchVotingPower(),
    ]).finally(() => setIsLoading(false))
  }, [refetchProposalCount, refetchVotingPower])

  return {
    proposals,
    votingPower: votingPower ?? 0n,
    proposalCount: Number(proposalCount ?? 0n),
    isLoading,
    vote,
    hasVoted,
    isVoting,
    createProposal,
    isCreating,
    refresh,
  }
}

